'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  MetaDefault,
  MonoDefault,
  MonoSmall,
} from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import type { ParsedEndpoint } from '../../ToolCallsRenderers/types';
import {
  parseEndpointContent,
  parseEndpointsListContent,
} from './endpoints-result-renderer';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import { formatCountedToolCallLabel } from '../shared/format-counted-tool-call-label';
import { renderHttpMethodText } from '../../../../../utils/http-badge.utils';

const toPreviewText = (endpoint: ParsedEndpoint): string => {
  if (endpoint.path) {
    return endpoint.path;
  }

  if (endpoint.endpointUrl) {
    return endpoint.endpointUrl;
  }

  if (endpoint.endpointDescription) {
    return endpoint.endpointDescription;
  }

  return endpoint.endpointId ?? 'Endpoint';
};

export function EndpointsFlatList({
  endpoints,
  onOpenObject,
}: {
  endpoints: ParsedEndpoint[];
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  return (
    <VStack alignItems="stretch" gap={1.25} width="100%" overflowX="hidden">
      {endpoints.map((endpoint, index) => {
        const key =
          endpoint.endpointId ??
          `${endpoint.path ?? endpoint.endpointUrl}-${index}`;
        const previewText = toPreviewText(endpoint);
        const canOpen = Boolean(onOpenObject && endpoint.endpointId);

        return (
          <HStack
            key={key}
            gap={1.5}
            alignItems="center"
            cursor={canOpen ? 'pointer' : 'default'}
            _hover={canOpen ? { color: 'fg.default' } : undefined}
            onClick={
              canOpen && endpoint.endpointId
                ? () =>
                    onOpenObject?.({
                      type: 'endpoint',
                      id: endpoint.endpointId as string,
                      label: previewText,
                      endpointUrl: endpoint.endpointUrl,
                    })
                : undefined
            }
          >
            {endpoint.method ? (
              <Box flexShrink={0}>{renderHttpMethodText(endpoint.method)}</Box>
            ) : null}
            {endpoint.path || endpoint.endpointUrl ? (
              <MonoDefault color="fg.default" truncate flex={1}>
                {previewText}
              </MonoDefault>
            ) : (
              <MetaDefault color="fg.default" truncate flex={1}>
                {previewText}
              </MetaDefault>
            )}
            {endpoint.score ? (
              <MonoSmall color="fg.muted">{endpoint.score}</MonoSmall>
            ) : null}
          </HStack>
        );
      })}
    </VStack>
  );
}

export function renderEndpointsCard(ctx: CardRenderContext) {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const output = ctx.toolCallData.output;
  const endpointListParsed = parseEndpointsListContent(output);
  const endpointSingleParsed = endpointListParsed
    ? null
    : parseEndpointContent(output);
  const endpoints = endpointListParsed
    ? endpointListParsed.endpoints
    : endpointSingleParsed
      ? [endpointSingleParsed.endpoint]
      : [];

  if (
    !isRunning &&
    endpoints.length === 0 &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  const displayText = isRunning
    ? ctx.labelPresent
    : endpoints.length > 0
      ? formatCountedToolCallLabel({
          label: ctx.labelPast,
          count: endpoints.length,
        })
      : ctx.labelPast;

  return (
    <InlineExecutionToolCard
      title={displayText}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      headerActions={getHITLArtifactHeaderActions(ctx)}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
    >
      {endpoints.length > 0 ? (
        <EndpointsFlatList
          endpoints={endpoints}
          onOpenObject={ctx.onOpenObject}
        />
      ) : null}
    </InlineExecutionToolCard>
  );
}
